import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendIcon from '@mui/icons-material/Send';
import {
  Stack,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MentionsInput, Mention } from 'react-mentions';

import {
  mentionListStyle,
  mentionStyle,
} from '../../../assets/styles/comments';
import { CommentReply } from '../../../interfaces/CommentReply';
import {
  fetchUserList,
  stringToColor,
} from '../../../services/commentsService';

import { useCurrentUser } from '~/services/api/users/endpoints';

interface CommentCardHeaderProps {
  reply: CommentReply;
  deleteReply: (id: number) => void;
  editReply: (replyID: number, text: string, userIDs: number[]) => void;
  markAsRead: (id: number) => void;
}

const CommentReplySection = ({
  reply,
  deleteReply,
  editReply,
  markAsRead,
}: CommentCardHeaderProps) => {
  const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [currentText, setCurrentText] = useState(reply.content);
  const [mentionedUserIDs, setMentionedUserIDs] = useState<number[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const open = Boolean(anchorMenu);
  const { id } = useCurrentUser().data ?? { id: -1 };
  const commentDate = dayjs(reply.updatedAt);

  const isUnread =
    reply.hasSeenUsers.findIndex((userID) => userID === id) === -1;

  const handleChangeText = (value: string) => {
    setCurrentText(value);
  };

  const handleChangeMentionedUsers = (idList: number[]) => {
    setMentionedUserIDs(idList);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMenu(null);
  };

  const handleDeleteReply = () => {
    handleCloseMenu();
    deleteReply(reply.id);
  };

  const handleToggleEditMode = () => {
    setEditMode((prevValue) => !prevValue);
    handleCloseMenu();
  };

  const handleCancelClicked = () => {
    setCurrentText(reply.content);
    handleToggleEditMode();
  };

  const handleSendClicked = () => {
    if (currentText !== reply.content) {
      editReply(reply.id, currentText, mentionedUserIDs);
    }
    handleToggleEditMode();
  };

  const handleToggleReadMode = () => {
    markAsRead(reply.id);
    handleCloseMenu();
  };

  useEffect(() => {
    if (editMode && inputRef) {
      inputRef.current?.focus();
    }
    setCurrentText(reply.content);
  }, [editMode, reply.content]);

  return (
    <Box marginTop={2}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Avatar
          alt={reply.authorDetails.full_name ?? 'Author'}
          sx={{
            backgroundColor: stringToColor(
              reply.authorDetails.full_name ?? 'Admin',
            ),
            width: 24,
            height: 24,
            fontSize: 14,
          }}
        >
          {reply.authorDetails.full_name
            ? reply.authorDetails.full_name[0]
            : 'U'}
        </Avatar>
        <Typography fontSize={14} fontWeight={500} color='#6D6D6D'>
          {reply.authorDetails.full_name ?? 'User'}
        </Typography>
        <Typography fontSize={14} fontWeight={400} color='#878787'>
          {commentDate.format('LT')}
        </Typography>
        <Typography fontSize={14} fontWeight={400} color='#878787'>
          {commentDate.format('MMM D')}
        </Typography>
        <Stack direction='row' width='68px' justifyContent='flex-end'>
          <IconButton
            size='small'
            disableRipple
            onClick={handleOpenMenu}
            sx={{
              backgroundColor: open ? '#2449FC' : 'transparent',
              borderRadius: '2px',
            }}
          >
            <MoreHorizIcon htmlColor={open ? 'white' : '#5d5d5d'} />
          </IconButton>
        </Stack>
      </Stack>
      <Box paddingY={1}>
        <MentionsInput
          onChange={(_, newValue, __, mentionList) => {
            handleChangeText(newValue);
            const idArray = mentionList.map((mention) => {
              return parseInt(mention.id);
            });
            handleChangeMentionedUsers(idArray);
          }}
          value={currentText}
          style={mentionListStyle}
          disabled={!editMode}
          inputRef={inputRef}
        >
          <Mention trigger='@' data={fetchUserList} style={mentionStyle} />
        </MentionsInput>
        {!editMode && reply.isEdited && (
          <Typography fontStyle='italic' fontSize={10} color='gray'>
            <FormattedMessage defaultMessage='Edited' />
          </Typography>
        )}
      </Box>
      {editMode && (
        <Stack
          direction='row'
          justifyContent='flex-end'
          width='100%'
          spacing={2}
          borderTop='1px solid #D6D6D6'
          paddingX='8px'
          paddingY='12px'
        >
          <Button
            onClick={handleCancelClicked}
            variant='text'
            color='darkGray'
            sx={{
              textTransform: 'none',
              padding: 0,
            }}
          >
            <FormattedMessage defaultMessage='Cancel' />
          </Button>
          <IconButton
            onClick={handleSendClicked}
            disabled={Boolean(!currentText)}
            sx={{
              backgroundColor: '#2449FC',
              '&:hover': {
                backgroundColor: '#1d3aca',
              },
              '&.Mui-disabled': {
                backgroundColor: '#D1D1D1',
              },
            }}
          >
            <SendIcon
              sx={{
                fontSize: 16,
                color: 'white',
              }}
            />
          </IconButton>
        </Stack>
      )}
      <Menu
        anchorEl={anchorMenu}
        open={open}
        onClose={handleCloseMenu}
        sx={{
          zIndex: 999999,
          '& .MuiPaper-root': {
            backgroundColor: '#454545',
            borderRadius: '4px',
          },
          '& .MuiMenuItem-root': {
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Schibsted Grotesk',
            '&:hover': {
              backgroundColor: '#2449FC',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleToggleReadMode()} disabled={!isUnread}>
          <FormattedMessage defaultMessage='Mark as read' />
        </MenuItem>
        {id === reply.authorDetails.id && !editMode && (
          <MenuItem onClick={handleToggleEditMode}>
            <FormattedMessage defaultMessage='Edit' />
          </MenuItem>
        )}
        {id === reply.authorDetails.id && editMode && (
          <MenuItem onClick={handleCancelClicked}>
            <FormattedMessage defaultMessage='Cancel Edit' />
          </MenuItem>
        )}
        {id === reply.authorDetails.id && (
          <MenuItem onClick={handleDeleteReply}>
            <FormattedMessage defaultMessage='Delete' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default CommentReplySection;
